import { useMemo, useRef } from 'react';

import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import { Route, Routes } from 'react-router-dom';

import { TopbarTitle } from '@modules/_shared/components/topbar/Topbar';
import { useLocale } from '@shared/hooks/locale';

import { EnquiryDrawer } from '../enquiries/EnquiryDrawer';
import { LeadDetailsDrawer } from '../lead-details/LeadDetailsDrawer';
import { ListingDrawer } from '../listings/ListingDrawer';
import EditTeamMemberDrawer from '../visibility-team/EditTeamMemberDrawer';

import { GRID_GAP, dashboardPageSx } from './constants';
import { DashboardDesktopView } from './DashboardDesktopView';
import { DashboardMobileView } from './DashboardMobileView';
import { DashboardUserCard } from './DashboardUserCard';
import { useDashboardModules } from './shared/useDashboardModules';
import { SubscriptionAlert } from './SubscriptionAlert';
import type { RetryableRef } from './types';

export const Dashboard = () => {
  const { t } = useLocale();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { isBrokerageEnabled } = useDashboardModules();

  const enquiriesRef = useRef<RetryableRef>(null);

  // `minmax(0, 1fr)` instead of bare `1fr`: bare `1fr` resolves to
  // `minmax(auto, 1fr)` so a single wide child (image, long token) blows
  // the track out and overflows horizontally on mobile.
  const gridSx = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: GRID_GAP,
      [theme.breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
      },
    }),
    [theme],
  );

  return (
    <>
      <TopbarTitle>{t('Dashboard')}</TopbarTitle>
      <SubscriptionAlert />
      <Box sx={dashboardPageSx}>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <DashboardUserCard />

          {isTablet ? (
            <DashboardMobileView enquiriesRef={enquiriesRef} />
          ) : (
            <Box sx={gridSx}>
              <DashboardDesktopView enquiriesRef={enquiriesRef} />
            </Box>
          )}
        </Stack>
      </Box>
      <Routes>
        <Route path="users/:userId/edit" element={<EditTeamMemberDrawer />} />
        {isBrokerageEnabled && (
          <Route
            path="enquiries/:enquiryId/*"
            element={
              <EnquiryDrawer onRefetch={() => enquiriesRef.current?.retry()} />
            }
          />
        )}
        <Route path="leads/:leadId/*" element={<LeadDetailsDrawer />} />
        <Route
          path="listings/:lotId/*"
          element={<ListingDrawer basePath="/dashboard/listings" />}
        />
      </Routes>
    </>
  );
};

export default Dashboard;
